import { useNavigate } from 'react-router-dom';
import { usePatientActions } from '../../../contexts/PatientActionsContext';
import { useRecentActions } from '../../../contexts/RecentActionsContext';
import type { PatientListItemType } from '../../../providers/patient/PatientsListProvider';
import { getActionRequiredRoute } from '../../../utils/actionRoutes';
import { ACTION_STYLES, hasActionRequired } from '../../../utils/constants';

interface ActionRequiredCellProps {
    patient: PatientListItemType;
}

export function ActionRequiredCell({ patient }: ActionRequiredCellProps) {
    const navigate = useNavigate();
    const { handleAction } = usePatientActions();
    const { recentActions } = useRecentActions();

    // A patient the current user recently resolved an action for, whose
    // requiredAction has since gone back to None on refetch, keeps its sorted
    // slot (see patientTableSort's sortByActionRequired) and shows a
    // completed indicator here instead of "No action needed" until
    // RecentActionsContext's global sliding window clears it.
    const hasNoCurrentAction = !hasActionRequired(patient.requiredAction);
    const recentlyCompletedAction = hasNoCurrentAction ? recentActions[patient.id] : undefined;

    if (hasNoCurrentAction) {
        return recentlyCompletedAction ? (
            <span
                className="font-semibold text-center text-sm text-green-500 h-5 block w-full truncate"
                role="status"
            >
                ✓ Action completed
            </span>
        ) : (
            <span className="font-normal text-center text-sm leading-5 tracking-[0.005em] text-gray-500 h-5 block w-full truncate">
                No action needed
            </span>
        );
    }

    return (
        <button
            className={`flex flex-row justify-center items-center px-3 py-2 w-full h-8 border ${
                ACTION_STYLES[patient.requiredAction ?? '']?.border ?? 'border-gray-300'
            } rounded-lg font-semibold text-sm ${
                ACTION_STYLES[patient.requiredAction ?? '']?.text ?? 'text-gray-800'
            } hover:bg-gray-100 group relative`}
            onClick={async () => {
                const action = patient.requiredAction ?? '';
                const patientId = patient.id ?? '';

                if (patientId) {
                    const handled = await handleAction(action, patientId);
                    if (!handled) {
                        const route = getActionRequiredRoute(action, patientId);
                        if (route) {
                            navigate(route);
                        }
                    }
                }
            }}
        >
            <span className="truncate max-w-full block">{patient.requiredAction}</span>
        </button>
    );
}
